import React, { useEffect } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";

// core components
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import Footer from "components/Footer/Footer.js";
import Sidebar from "components/Sidebar/Sidebar.js";
// import FixedPlugin from "components/FixedPlugin/FixedPlugin.js";
import LoadingModal from "components/LoadingModal/LoadingModal.js";

import routes from "routes.js";

// import logo from "assets/img/react-logo.png";
import r2h_logo from "assets/img/R2H_logo_1117_02_outline-15.png";
import oa_logo from "assets/img/OceanAtlas_logo.png";
import { BackgroundColorContext } from "contexts/BackgroundColorContext";
import { usePlatform } from "contexts/PlatformContext";
import JWTManager from "constant/JWTManager";
import { canAccessRoute, firstAccessibleRoute } from "utils/permissions";
import ForcePasswordChange from "views/MainLayout/ForcePasswordChange";

function Admin(props) {  
  const location = useLocation();
  const { getPlatformInfo } = usePlatform();
  const navigate = useNavigate();
  const mainPanelRef = React.useRef(null);
  const [sidebarOpened, setsidebarOpened] = React.useState(
    document.documentElement.className.indexOf("nav-open") !== -1
  );
  const [sidebarVisible, setSidebarVisible] = React.useState(true);
  
  // Handle redirection based on user permission
  useEffect(() => {
    // Only proceed if user data is loaded
    if (props.user) {
      const currentPath = location.pathname;

      // v2.32.0：判準改成 permission AND requiredCap（見 utils/permissions.js）
      const landing = firstAccessibleRoute(props.user, routes);
      const landingPagePath = landing ? `/admin${landing.path}` : "/admin";

      // If we're at the root admin path, redirect to appropriate landing page
      if (currentPath === '/admin' || currentPath === '/admin/') {
        navigate(landingPagePath, { replace: true });
        return;
      }

      // Check if current path is accessible to this user
      const currentRouteInfo = routes.find(route =>
        currentPath === `/admin${route.path}`
      );

      // 進不去就導回自己的落地頁（例如貓生直接輸入 /admin/revenue）
      if (currentRouteInfo && !canAccessRoute(props.user, currentRouteInfo)) {
        navigate(landingPagePath, { replace: true });
      }
    }
  }, [props.user, location.pathname, navigate, routes]);
  
  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainPanelRef.current) {
      mainPanelRef.current.scrollTop = 0;
    }
  }, [location]);
  
  // Token 過期事件監聽器
  React.useEffect(() => {
    const handleTokenExpired = () => {
      console.log('[Admin] Token expired event received, redirecting to login');
      JWTManager.clearToken();
      window.location.href = '/login';
    };
    
    // 監聽 JWTManager 發出的 token 過期事件
    window.addEventListener('tokenExpired', handleTokenExpired);
    
    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
    };
  }, []);

  // Authentication check effect
  React.useEffect(() => {
    const checkAuthentication = async () => {
      // Check if login is false in Redux state
      if (!props.login) {
        const access_token = JWTManager.getAccessToken();
        const refresh_token = JWTManager.getRefreshToken();

        // If tokens are missing entirely, redirect to login
        if (!access_token || !refresh_token) {
          window.location.href = '/login';
          console.log("No tokens found, redirecting to login");
          return;
        }
        
        // Check if token is valid (not expired) or can be refreshed
        if (JWTManager.isTokenExpired()) {
          // Token is expired, try to refresh
          if (refresh_token) {
            try {
              await JWTManager.refreshToken();
              console.log("Token refreshed successfully");
              props.succeedLogin();
              props.getUser();
              props.getServerConfig();
            } catch (error) {
              console.log("Token refresh failed, redirecting to login");
              JWTManager.clearToken();
              window.location.href = '/login';
              return;
            }
          } else {
            // No valid refresh token, redirect to login
            window.location.href = '/login';
            console.log("Token expired and no refresh token, redirecting to login");
          }
        } else {
          // Token exists and is not expired, restore session
          props.succeedLogin();
          props.getUser();
          props.getServerConfig();
          console.log("Valid token found, restoring session");
        }
      } else if (!props.user) {
        // Logged in but no user data yet
        props.getUser();
        props.getServerConfig();
      }
    };

    checkAuthentication();
  }, [props.login]); // Only run when login state changes

  // this function opens and closes the sidebar on small devices
  const toggleSidebar = () => {
    document.documentElement.classList.toggle("nav-open");
    setsidebarOpened(!sidebarOpened);
  };

  // this function hides/shows desktop sidebar
  const toggleSidebarVisibility = () => {
    const next = !sidebarVisible;
    setSidebarVisible(next);
  };
  
  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      let component = prop.component
      if (canAccessRoute(props.user, prop)) {
        component = prop.component
      } else {
        component = (
          <div className="content">
            <h1>You are not allowed to access this page.</h1>
          </div>
        )
      }
      if (prop.layout === "/admin") {
        return (
          <Route path={prop.path} element={component} key={key} exact />
        );
      } else {
        return null;
      }
    });
  };
  
  // header 左上角顯示「<平台> 營運後臺」。
  //
  // 🔴 **原本這裡回傳的是「目前頁面的 route name」，而且是壞的**：
  //    它用 `indexOf(...) !== -1` 做子字串比對又回傳第一個命中的，
  //    而 `/tiny`（數據總覽）排在 `/tiny-*` 前面 → **每一個 `/admin/tiny-*`
  //    頁面都顯示「數據總覽」**（在 JP Banker 試算頁上寫著數據總覽）。
  //
  // 🚫 修好它也沒有意義：**每一頁 content 裡本來就有自己的標題區**
  //    （雙週獎池分析 / EcoBot 數據分析 / JP Banker 試算 …），
  //    header 再寫一次頁名純粹是重複。改成顯示平台身分 —— 那是頁內標題
  //    **不會**告訴你的資訊，而右上角帳號 + 這裡就湊成「誰、在哪個平台」。
  //
  // 2026-09-18：Brad 決定 header 固定顯示「聯盟營運後臺」，不再帶平台名 ——
  //    「現在在哪個平台」由側欄的平台切換器表達，header 只講這是什麼系統。
  const getBrandText = () => "聯盟營運後臺";

  // Get the default landing page based on user permission
  const getDefaultRoute = () => {
    if (!props.user) {
      return "/admin";  // Will be handled by the useEffect
    }
    
    // v2.32.0：同上，permission AND requiredCap
    const landing = firstAccessibleRoute(props.user, routes);

    // Return the first accessible route or fallback to admin root
    return landing
      ? `/admin${landing.path}` 
      : "/admin";
  };

  return (
    <BackgroundColorContext.Consumer>
      {({ color, changeColor }) => (
        props.user && props.user.must_change_password ? (
          // v2.32.0：首次登入（或被重設密碼）時，什麼都不給看，先改密碼。
          // 這只是入口 —— 後端 before_request 會把其他 API 全部 403，
          // 所以繞過這個畫面也沒用。
          <ForcePasswordChange username={props.user.username} />
        ) : (
        <React.Fragment>
          <div className={`wrapper ${sidebarVisible ? "" : "sidebar-manual-hidden"}`}>
            <Sidebar
              config = {props.config}
              user={props.user}
              routes={routes}
              logo={{
                outterLink: "https://www.rake2high.com/",
                text: "Rake2High",
                imgSrc: r2h_logo,
              }}
              logo2={{
                outterLink: "https://www.oceanatlas.net/",
                text: "Ocean Atlas",
                imgSrc: oa_logo,
              }}
              toggleSidebar={toggleSidebar}
            />
            <div className="main-panel" ref={mainPanelRef} data={color}>
              <AdminNavbar
                user={props.user}
                brandText={getBrandText()}
                toggleSidebar={toggleSidebar}
                toggleSidebarVisibility={toggleSidebarVisibility}
                sidebarOpened={sidebarOpened}
                sidebarVisible={sidebarVisible}
              />
              <Routes>
                {getRoutes(routes)}
                <Route
                  path="/"
                  element={<Navigate to={getDefaultRoute()} replace />}
                />
              </Routes>
              {
                // we don't want the Footer to be rendered on map page
                location.pathname === "/admin/maps" ? null : <Footer fluid />
              }
            </div>
          </div>
          {/* <FixedPlugin bgColor={color} handleBgClick={changeColor} /> */}
          
          {/* Add the LoadingModal component */}
          <LoadingModal />
          <style>{`
            @media (min-width: 992px) {
              /* 🔴 transition 要放在**基礎樣式**上，不能只放在收合狀態裡。
                 原本 transition 只寫在 .sidebar-manual-hidden .sidebar，
                 所以收合時（class 加上去）有動畫、**展開時 class 一被移除，
                 元素就不再命中那條規則、連帶失去 transition → 直接彈回去**。
                 這是 CSS transition 最常見的單向動畫成因。 */
              .wrapper .sidebar {
                transition: transform 0.28s ease, opacity 0.2s ease;
              }
              .wrapper .main-panel > .content {
                transition: padding-left 0.28s ease;
              }
              .wrapper.sidebar-manual-hidden .sidebar {
                transform: translateX(-280px);
                opacity: 0;
                pointer-events: none;
              }
              .wrapper.sidebar-manual-hidden .main-panel {
                width: 100%;
                max-width: 100%;
              }
              .wrapper.sidebar-manual-hidden .main-panel > .content {
                padding-left: 30px;
              }
              .wrapper.sidebar-manual-hidden .main-panel > .navbar {
                width: 100%;
              }
              .wrapper.sidebar-manual-hidden .footer {
                padding-left: 0;
              }
            }
          `}</style>
          
          {/* Fixed Version Display */}
          <div
            style={{
              position: 'fixed',
              bottom: '15px',
              right: '20px',
              background: 'rgba(0, 0, 0, 0.6)',
              color: 'rgba(255, 255, 255, 0.8)',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '0.85rem',
              fontWeight: '500',
              letterSpacing: '0.5px',
              zIndex: 999,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
            }}
          >
            <i className="fa fa-code" style={{ marginRight: '8px', color: '#2fbf2f' }}></i>
            Version {props.config?.version || '?'}
          </div>

        </React.Fragment>
        )
      )}
    </BackgroundColorContext.Consumer>
  );
}

export default Admin;